import { useQuery } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { type CiPortalConfig } from "../../shared/types";
import { Button } from "../components/Button";
import { ErrorBanner } from "../components/Feedback";
import { apiRequest } from "../lib/api";
import { authClient } from "../lib/auth-client";

/** Sign-in page: one button per identity provider, plus a local-only password form. */
export const SignInPage = () => {
  const config = useQuery({
    queryKey: ["config"],
    queryFn: () => apiRequest<CiPortalConfig>({ path: "/api/config" }),
  });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const callbackURL =
    window.location.pathname === "/" ? "/mocks" : window.location.pathname;

  const signInWith = async (provider: string) => {
    setBusy(true);
    setError(null);
    // Redirects to the provider; the result comes back to /api/auth/callback/<provider>.
    const result = await authClient.signIn.social({ provider, callbackURL });
    if (result.error) {
      setError(new Error(result.error.message ?? "Sign-in failed"));
      setBusy(false);
    }
  };

  const signInWithPassword = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const email = String(form.get("email"));
    const password = String(form.get("password"));
    setBusy(true);
    setError(null);
    const signIn = await authClient.signIn.email({ email, password });
    // First local sign-in creates the account.
    const failure =
      signIn.error?.status === 401
        ? (
            await authClient.signUp.email({
              email,
              password,
              name: email.split("@")[0] ?? email,
            })
          ).error
        : signIn.error;
    if (failure) {
      setError(new Error(failure.message ?? "Sign-in failed"));
      setBusy(false);
      return;
    }
    window.location.assign(callbackURL);
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="card w-full max-w-sm p-8">
        <div className="mb-6 flex items-center gap-3">
          <img src="/favicon.svg" alt="" className="h-9 w-9" />
          <div>
            <h1 className="text-lg font-semibold">Pillar CI</h1>
            <p className="text-sm text-slate-500">
              API mocks, test results and coverage
            </p>
          </div>
        </div>
        <div className="space-y-3">
          <ErrorBanner error={error ?? config.error} />
          {config.data?.providers.map((provider) => (
            <Button
              key={provider.id}
              className="w-full"
              disabled={busy}
              onClick={() => signInWith(provider.id)}
            >
              {provider.label}
            </Button>
          ))}
          {config.data?.devPasswordLogin ? (
            <form
              className="space-y-3 border-t border-slate-200 pt-4"
              onSubmit={signInWithPassword}
            >
              <p className="text-xs text-amber-700">
                Local development sign-in (disabled on AWS).
              </p>
              <input
                className="input"
                name="email"
                type="email"
                placeholder="Email"
                required
                autoComplete="username"
              />
              <input
                className="input"
                name="password"
                type="password"
                placeholder="Password (8+ characters)"
                minLength={8}
                required
                autoComplete="current-password"
              />
              <Button
                type="submit"
                variant="secondary"
                className="w-full"
                disabled={busy}
              >
                Sign in locally
              </Button>
            </form>
          ) : null}
        </div>
      </div>
    </div>
  );
};
